
import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const OFFICE_API =
  "https://guardiancloudcare.com/backend/api/get_supervisor_offices.php";

export interface SelectedLocation {
  latitude: number;
  longitude: number;
  address: string;
  barangay: string;
  municipality: string;
  province: string;
}

interface LocationPickerProps {
  onLocationSelect: (location: SelectedLocation) => void;
}

interface GeoFeature {
  id?: string;
  text?: string;
  place_name?: string;
  properties?: Record<string, any>;
}

interface SupervisorOffice {
  uid: string;
  name: string;
  supervisor_name: string;
  municipality: string | null;
  province: string | null;
  latitude: number;
  longitude: number;
}

interface OfficeResponse {
  success: boolean;
  offices: SupervisorOffice[];
  error?: string;
}

export default function LocationPicker({
  onLocationSelect,
}: LocationPickerProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const selectionMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const callbackRef = useRef(onLocationSelect);
  const requestIdRef = useRef(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [officeCount, setOfficeCount] = useState(0);

  useEffect(() => {
    callbackRef.current = onLocationSelect;
  }, [onLocationSelect]);

  useEffect(() => {
    if (!mapContainer.current) return;

    if (!MAPBOX_TOKEN) {
      setError(
        "Mapbox token is missing. Add VITE_MAPBOX_TOKEN to your .env file."
      );
      return;
    }

    mapboxgl.accessToken = MAPBOX_TOKEN;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [121.4737, 14.5995],
      zoom: 9,
    });

    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    let cancelled = false;
    let officeMarkers: mapboxgl.Marker[] = [];
    let officeRefreshInProgress = false;

    const getName = (feature?: GeoFeature): string => {
      const properties = feature?.properties ?? {};

      return (
        properties.name_preferred ||
        properties.name ||
        feature?.text ||
        ""
      );
    };

    const getContextName = (
      feature: GeoFeature | undefined,
      key: string
    ): string => {
      const context = feature?.properties?.context ?? {};
      const value = context[key];

      if (typeof value === "string") return value;

      if (value && typeof value === "object") {
        return value.name || "";
      }

      return "";
    };

    // Load all approved supervisor office locations from PHP.
    const loadOfficeMarkers = async () => {
      if (officeRefreshInProgress || cancelled) return;

      officeRefreshInProgress = true;

      try {
        const response = await fetch(OFFICE_API, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(
            `Office API returned HTTP ${response.status}`
          );
        }

        const data: OfficeResponse = await response.json();

        if (
          !data.success ||
          !Array.isArray(data.offices)
        ) {
          throw new Error(
            data.error || "Invalid supervisor office response."
          );
        }

        if (cancelled) return;

        const uniqueOffices = new Map<
          string,
          SupervisorOffice
        >();

        for (const office of data.offices) {
          const latitude = Number(office.latitude);
          const longitude = Number(office.longitude);

          if (
            !Number.isFinite(latitude) ||
            !Number.isFinite(longitude) ||
            latitude < -90 ||
            latitude > 90 ||
            longitude < -180 ||
            longitude > 180
          ) {
            continue;
          }

          // One pin per distinct coordinate.
          const coordinateKey =
            `${latitude.toFixed(6)},${longitude.toFixed(6)}`;

          if (!uniqueOffices.has(coordinateKey)) {
            uniqueOffices.set(coordinateKey, {
              ...office,
              latitude,
              longitude,
            });
          }
        }

        // Remove the previous office markers before redrawing.
        officeMarkers.forEach((marker) => marker.remove());
        officeMarkers = [];

        for (const office of uniqueOffices.values()) {
          if (cancelled) break;

          const popupContent = document.createElement("div");
          popupContent.className = "p-2";

          const title = document.createElement("strong");
          title.textContent = office.name || "DSWD Office";

          const location = document.createElement("p");
          location.textContent = [
            office.municipality,
            office.province,
          ]
            .filter(Boolean)
            .join(", ");

          const supervisor = document.createElement("p");
          supervisor.textContent =
            `Supervisor: ${office.supervisor_name || "Not specified"}`;

          popupContent.append(
            title,
            location,
            supervisor
          );

          const popup = new mapboxgl.Popup({
            offset: 25,
            maxWidth: "280px",
          }).setDOMContent(popupContent);

          const officeMarker = new mapboxgl.Marker({
            color: "#2563eb",
          })
            .setLngLat([
              office.longitude,
              office.latitude,
            ])
            .setPopup(popup)
            .addTo(map);

          officeMarkers.push(officeMarker);
        }

        setOfficeCount(officeMarkers.length);

        console.info(
          `Loaded ${officeMarkers.length} supervisor office pin(s).`
        );
      } catch (err) {
        if (!cancelled) {
          console.error(
            "Unable to load supervisor office markers:",
            err
          );

          setError(
            "Unable to load DSWD office pins. Check the office API and browser console."
          );
        }
      } finally {
        officeRefreshInProgress = false;
      }
    };

    const handleMapLoad = () => {
      void loadOfficeMarkers();
    };

    // Refresh when this browser tab becomes active.
    const handleVisibilityChange = () => {
      if (!document.hidden && !cancelled) {
        void loadOfficeMarkers();
      }
    };

    map.on("load", handleMapLoad);

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    // Keep the original map-click and reverse-geocoding behavior.
    const handleMapClick = async (
      event: mapboxgl.MapMouseEvent
    ) => {
      const { lng, lat } = event.lngLat;
      const requestId = ++requestIdRef.current;

      selectionMarkerRef.current?.remove();

      selectionMarkerRef.current = new mapboxgl.Marker({
        color: "#f97316",
      })
        .setLngLat([lng, lat])
        .addTo(map);

      setLoading(true);
      setError("");

      try {
        const url = new URL(
          "https://api.mapbox.com/search/geocode/v6/reverse"
        );

        url.searchParams.set("longitude", String(lng));
        url.searchParams.set("latitude", String(lat));
        url.searchParams.set("access_token", MAPBOX_TOKEN);
        url.searchParams.set("language", "en");

        const response = await fetch(url.toString());
        const responseText = await response.text();

        let data: any;

        try {
          data = JSON.parse(responseText);
        } catch {
          throw new Error(
            "Mapbox returned an invalid response."
          );
        }

        if (!response.ok) {
          console.error(
            "Mapbox reverse geocoding error:",
            data
          );

          throw new Error(
            data.message ||
              `Address lookup failed (HTTP ${response.status}).`
          );
        }

        if (
          cancelled ||
          requestId !== requestIdRef.current
        ) {
          return;
        }

        const features: GeoFeature[] = data.features ?? [];

        if (features.length === 0) {
          throw new Error(
            "No address was found for this point. Please select another location or enter the address manually."
          );
        }

        const primary = features[0];
        const properties = primary.properties ?? {};

        const address =
          properties.full_address ||
          properties.place_formatted ||
          properties.name_preferred ||
          properties.name ||
          primary.place_name ||
          `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

        const addressParts = address
          .split(",")
          .map((part: string) => part.trim())
          .filter(Boolean);

        const localityFeature = features.find(
          (feature) =>
            feature.properties?.feature_type === "locality"
        );

        const placeFeature = features.find(
          (feature) =>
            feature.properties?.feature_type === "place"
        );

        const neighborhoodFeature = features.find(
          (feature) =>
            feature.properties?.feature_type === "neighborhood"
        );

        const regionFeature = features.find(
          (feature) =>
            feature.properties?.feature_type === "region"
        );

        const municipality =
          getName(localityFeature) ||
          getContextName(primary, "locality") ||
          getName(placeFeature) ||
          getContextName(primary, "place") ||
          addressParts[1] ||
          "";

        const province =
          getName(regionFeature) ||
          getContextName(primary, "region") ||
          addressParts[2] ||
          "";

        const firstAddressPart = addressParts[0] || "";

        const explicitBarangay = firstAddressPart.match(
          /\bBarangay\s+(.+)$/i
        );

        const barangay =
          getName(neighborhoodFeature) ||
          getContextName(primary, "neighborhood") ||
          (explicitBarangay
            ? explicitBarangay[1].trim()
            : "");

        const selectedLocation: SelectedLocation = {
          latitude: lat,
          longitude: lng,
          address,
          barangay,
          municipality,
          province,
        };

        // Pass the complete result instead of empty address fields.
        callbackRef.current(selectedLocation);

        if (!barangay || !municipality || !province) {
          setError(
            "Location selected. Mapbox did not return all address details. Please verify the barangay, municipality, and province."
          );
        }
      } catch (err) {
        if (
          cancelled ||
          requestId !== requestIdRef.current
        ) {
          return;
        }

        console.error(
          "Reverse geocoding failed:",
          err
        );

        setError(
          err instanceof Error
            ? err.message
            : "Unable to retrieve this address."
        );
      } finally {
        if (
          !cancelled &&
          requestId === requestIdRef.current
        ) {
          setLoading(false);
        }
      }
    };

    map.on("click", handleMapClick);

    return () => {
      cancelled = true;
      requestIdRef.current++;

      map.off("load", handleMapLoad);
      map.off("click", handleMapClick);

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      selectionMarkerRef.current?.remove();
      selectionMarkerRef.current = null;

      officeMarkers.forEach((marker) => marker.remove());
      officeMarkers = [];

      map.remove();
    };
  }, []);

  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-semibold text-gray-800">
          Select Office Location
        </h3>

        <p className="text-sm text-gray-500">
          Blue pins show saved DSWD supervisor offices.
          Click a blue pin to view its details. Click another
          location to select a location for this form.
        </p>

        <p className="text-xs text-blue-700">
          Supervisor offices displayed: {officeCount}
        </p>
      </div>

      <div
        ref={mapContainer}
        className="h-[380px] w-full overflow-hidden rounded-xl border border-gray-200"
      />

      {loading && (
        <p className="text-sm text-orange-600">
          Retrieving address from map...
        </p>
      )}

      {error && (
        <p className="text-sm text-amber-700">
          {error}
        </p>
      )}
    </div>
  );
}
